import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import { parseDate, getLocalTimeZone } from '@internationalized/date'
import Skeleton from 'react-loading-skeleton'

import { FulfillmentType } from 'src/apollo/onlineOrdering'
import { t } from 'src/translations'

import { toDayString } from 'shared/js/timeUtils'

import { useCart } from 'public/components/online_ordering/CartContext'
import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'
import { useTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext'

import ScheduleShipmentModal from './ScheduleShipmentModal'
import {
  calculateArrivalDate,
  calculateArrivalDateFromString,
  calculateMinArrivalDate,
  calculateShipDate
} from './shippingDateUtils'

interface ShippingDateSectionProps {
  estimatedDays?: number
  rateSelectionLoading?: boolean
}

const ShippingDateSection = ({
  estimatedDays,
  rateSelectionLoading = false
}: ShippingDateSectionProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [dateError, setDateError] = useState<string | null>(null)
  const [syncLoading, setSyncLoading] = useState(false)
  const { cart } = useCart()
  const {
    updateCartFulfillment,
    fulfillmentData,
    selectedDiningOptionBehaviorSchedules,
    selectedDiningOptionBehavior
  } = useFulfillment()
  const {
    getFilteredFutureSchedules,
    itemInCartWithMaxLeadTime,
    getEarliestFulfillmentTime
  } = useTimeBasedRules()

  const prevEstimatedDaysRef = useRef<number | undefined>(estimatedDays)
  const timezone = getLocalTimeZone()

  const isLoading = rateSelectionLoading || syncLoading

  const isFutureShipment = useMemo(
    () => cart?.fulfillmentType === FulfillmentType.Future,
    [cart]
  )

  // Get future schedules for validation
  const futureSchedules = useMemo(
    () =>
      getFilteredFutureSchedules(
        getEarliestFulfillmentTime(
          itemInCartWithMaxLeadTime?.itemGuid,
          selectedDiningOptionBehavior
        ),
        selectedDiningOptionBehaviorSchedules?.futureScheduleDates ?? [],
        selectedDiningOptionBehavior
      ),
    [
      selectedDiningOptionBehaviorSchedules,
      selectedDiningOptionBehavior,
      getFilteredFutureSchedules,
      getEarliestFulfillmentTime,
      itemInCartWithMaxLeadTime?.itemGuid
    ]
  )

  // Handle estimated days change - try to preserve the arrival date
  useEffect(() => {
    const prevEstimatedDays = prevEstimatedDaysRef.current
    prevEstimatedDaysRef.current = estimatedDays

    // Only react to actual changes, not initial render
    if (prevEstimatedDays === estimatedDays) return
    if (prevEstimatedDays === undefined || estimatedDays === undefined) return

    // Only process if we have a future fulfillment date
    if (!isFutureShipment || !cart?.fulfillmentDateTime) {
      return
    }

    // Calculate the arrival date based on the OLD estimated days
    const currentShipDateStr = cart.fulfillmentDateTime.split('T')[0]
    if (!currentShipDateStr) return

    const currentShipDate = parseDate(currentShipDateStr)
    const currentArrivalDate = calculateArrivalDate(
      currentShipDate,
      prevEstimatedDays,
      timezone
    )

    // Calculate the new ship date needed to keep the same arrival date
    const newShipDate = calculateShipDate(
      currentArrivalDate,
      estimatedDays,
      timezone
    )
    const newShipDateStr = newShipDate.toString()

    // Check if the new ship date has a valid schedule
    const schedule = futureSchedules?.find(
      (s) => s.times.length > 0 && s.date.startsWith(newShipDateStr)
    )

    // Check against minDate (arrival date must be >= minDate)
    const minDate = calculateMinArrivalDate(estimatedDays, timezone)
    const isArrivalDateValid = currentArrivalDate.compare(minDate) >= 0

    if (!isArrivalDateValid || !schedule) {
      // Can't keep the same arrival date - open modal with error
      setDateError(
        t('shipping-section.shipping-option-date-error') ||
          'The selected shipping option cannot deliver by your chosen date. Please select a new date.'
      )
      setIsModalOpen(true)
    } else {
      // Valid - update the fulfillment date to maintain the same arrival date
      const firstTime = schedule.times[0]?.time
      if (firstTime) {
        const syncFulfillment = async () => {
          setSyncLoading(true)
          try {
            await updateCartFulfillment({
              fulfillmentType: FulfillmentType.Future,
              fulfillmentDateTime: firstTime,
              diningOptionBehavior:
                fulfillmentData?.cartFulfillmentData?.diningOptionBehavior,
              deliveryInfo: fulfillmentData?.cartFulfillmentData?.deliveryInfo
            })
            setDateError(null)
          } finally {
            setSyncLoading(false)
          }
        }
        syncFulfillment()
      }
    }
  }, [
    estimatedDays,
    cart?.fulfillmentDateTime,
    cart?.fulfillmentType,
    isFutureShipment,
    timezone,
    futureSchedules,
    updateCartFulfillment,
    fulfillmentData?.cartFulfillmentData?.diningOptionBehavior,
    fulfillmentData?.cartFulfillmentData?.deliveryInfo
  ])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setDateError(null)
  }, [])

  const getShippingDateDisplay = () => {
    if (!cart) return t('shipping-section.select-date')

    if (isFutureShipment && cart?.fulfillmentDateTime) {
      // Convert ship date to arrival date for display
      const arrivalDateTime = calculateArrivalDateFromString(
        cart.fulfillmentDateTime,
        estimatedDays
      )
      return toDayString(arrivalDateTime)
    }

    return t('shipping-section.asap')
  }

  return (
    <div className='shippingDateSection'>
      <button
        className='shippingDateButton'
        onClick={() => setIsModalOpen(true)}
        type='button'
        data-testid='shipping-date-button'
        disabled={isLoading}
      >
        <div className='shippingDateContent'>
          {isLoading ? (
            <div
              className='shippingDateValue'
              data-testid='shipping-date-loading'
            >
              <Skeleton width={120} height={20} />
            </div>
          ) : (
            <>
              <div className='shippingDateValue'>
                {getShippingDateDisplay()}
              </div>
              <div className='shippingDateSubtitle'>
                {isFutureShipment && t('shipping-section.estimated-ship-date')}
              </div>
            </>
          )}
        </div>
        {!isLoading && (
          <ChevronRightIcon
            size='xs'
            accessibility='semantic'
            aria-label={t('fulfillment-time-summary.details')}
          />
        )}
      </button>
      <ScheduleShipmentModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        estimatedDays={estimatedDays}
        dateError={dateError}
      />
    </div>
  )
}

export default ShippingDateSection
